import EditIcon from "@mui/icons-material/Edit";
import { Box, CircularProgress, Snackbar, Tooltip, Typography } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import React, { useState } from "react";
import { useDispatch } from "react-redux";

import { resetUpdateScheduleConfiguration } from "../../../../store/gat-admin-slice";
import { WorkerMovementEligibility } from "../../../../store/gat-admin-slice-types";
import useWorkerMovementController from "./controllers/use-worker-movement-controller";
import WorkerMovementEditDialog from "./worker-movement-edit-dialog";

interface WorkerMovementSettingsProps {
	locationId: number;
}

type MovementScope = "samePatternMovement" | "crossPatternMovement";

const WorkerMovementSettings: React.FC<WorkerMovementSettingsProps> = ({ locationId }) => {
	const dispatch = useDispatch();
	const [
		{
			scheduleConfiguration,
			scheduleConfigurationStatus,
			updateScheduleConfigurationStatus,
			updateScheduleConfigurationError,
			workerTypes,
			workerSubTypes,
		},
		{ handleSave },
	] = useWorkerMovementController(locationId);

	const [editingScope, setEditingScope] = useState<MovementScope | null>(null);

	const defaultWorkerMovementEligibility: WorkerMovementEligibility = {
		samePatternMovement: [],
		crossPatternMovement: [],
	};

	const workerMovementEligibility =
		scheduleConfiguration?.movementEligibility || defaultWorkerMovementEligibility;

	const handleEditClick = (scope: MovementScope) => {
		setEditingScope(scope);
	};

	const handleDialogClose = () => {
		setEditingScope(null);
	};

	const handleDialogSave = (updatedEligibility: WorkerMovementEligibility) => {
		handleSave(updatedEligibility);
	};

	const getDisplayText = (scope: MovementScope) => {
		const movements = workerMovementEligibility[scope];

		if (!movements || movements.length === 0) {
			return (
				<Typography variant="body2" sx={{ fontSize: "14px", color: "rgba(0, 0, 0, 0.38)" }}>
					No worker types allowed
				</Typography>
			);
		}

		return (
			<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
				{movements.map((movement, index) => {
					const workerType = workerTypes.find((wt) => wt.name === movement.workerType);
					const typeText = workerType?.text || movement.workerType;

					if (movement.allSubTypesAllowed) {
						return (
							<Typography
								key={`${movement.workerType}-${index}`}
								variant="body2"
								sx={{
									fontSize: "14px",
									color: "rgba(0, 0, 0, 0.87)",
								}}
							>
								<Box component="span" sx={{ fontWeight: 500 }}>
									{typeText}
								</Box>
								<Box component="span" sx={{ color: "rgba(0, 0, 0, 0.6)", ml: 0.5 }}>
									(All subtypes)
								</Box>
							</Typography>
						);
					}

					if (movement.subTypes.length > 0) {
						return (
							<Typography
								key={`${movement.workerType}-${index}`}
								variant="body2"
								sx={{
									fontSize: "14px",
									color: "rgba(0, 0, 0, 0.87)",
								}}
							>
								<Box component="span" sx={{ fontWeight: 500 }}>
									{typeText}
								</Box>
								<Box component="span" sx={{ color: "rgba(0, 0, 0, 0.6)", ml: 0.5 }}>
									({movement.subTypes.join(", ")})
								</Box>
							</Typography>
						);
					}

					return (
						<Typography
							key={`${movement.workerType}-${index}`}
							variant="body2"
							sx={{
								fontSize: "14px",
								color: "rgba(0, 0, 0, 0.87)",
								fontWeight: 500,
							}}
						>
							{typeText}
						</Typography>
					);
				})}
			</Box>
		);
	};

	if (scheduleConfigurationStatus === "pending") {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
				<CircularProgress size={24} />
			</Box>
		);
	}

	if (scheduleConfigurationStatus === "error") {
		return (
			<Typography color="error" variant="body2">
				Failed to load worker movement settings
			</Typography>
		);
	}

	return (
		<Box
			sx={{
				backgroundColor: "#FFFFFF",
				borderRadius: "8px",
				padding: "16px",
				mt: 2,
			}}
		>
			<Typography
				variant="subtitle2"
				sx={{
					fontSize: "16px",
					color: "rgba(0, 0, 0, 0.87)",
					mb: 2,
				}}
			>
				Scope of Worker Movement
			</Typography>
			<Typography
				variant="caption"
				sx={{
					fontSize: "12px",
					color: "rgba(0, 0, 0, 0.6)",
					display: "block",
					mb: 3,
				}}
			>
				Configure which Worker Types (or Sub Types) can be moved while generating a schedule
			</Typography>

			{/* Table Header */}
			<Box
				sx={{
					display: "flex",
					borderBottom: "2px solid rgba(0, 0, 0, 0.12)",
					pb: 1,
					// mb: 2,
				}}
			>
				<Box sx={{ width: "300px" }}>
					<Typography sx={{ fontSize: "14px", fontWeight: 600, color: "rgba(0, 0, 0, 0.87)" }}>
						Scope of Movement
					</Typography>
				</Box>
				<Box sx={{ flex: 1 }}>
					<Typography sx={{ fontSize: "14px", fontWeight: 600, color: "rgba(0, 0, 0, 0.87)" }}>
						Allowed Worker Types & Subtypes
					</Typography>
				</Box>
				<Box sx={{ width: "80px", textAlign: "center" }}>
					<Typography sx={{ fontSize: "14px", fontWeight: 600, color: "rgba(0, 0, 0, 0.87)" }}>
						Actions
					</Typography>
				</Box>
			</Box>

			{/* Table Rows */}
			<Box>
				{/* Job/Department Movement Row */}
				<Box
					sx={{
						display: "flex",
						alignItems: "flex-start",
						minHeight: "56px",
						py: 1.5,
						borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
					}}
				>
					<Box sx={{ width: "300px" }}>
						<Typography sx={{ fontSize: "14px", fontWeight: 500 }}>Job/Department Movement</Typography>
					</Box>
					<Box sx={{ flex: 1, pr: 2 }}>{getDisplayText("samePatternMovement")}</Box>
					<Box sx={{ width: "80px", textAlign: "center" }}>
						<Tooltip title="Configure">
							<EditIcon
								sx={{
									fontSize: "20px",
									cursor: "pointer",
									color: "#6c6c6c",
									"&:hover": {
										color: "#000000",
									},
								}}
								onClick={() => handleEditClick("samePatternMovement")}
							/>
						</Tooltip>
					</Box>
				</Box>

				{/* Shift Movement Row */}
				<Box
					sx={{
						display: "flex",
						alignItems: "flex-start",
						minHeight: "56px",
						py: 1.5,
					}}
				>
					<Box sx={{ width: "300px" }}>
						<Typography sx={{ fontSize: "14px", fontWeight: 500 }}>Shift Movement</Typography>
					</Box>
					<Box sx={{ flex: 1, pr: 2 }}>{getDisplayText("crossPatternMovement")}</Box>
					<Box sx={{ width: "80px", textAlign: "center" }}>
						<Tooltip title="Configure">
							<EditIcon
								sx={{
									fontSize: "20px",
									cursor: "pointer",
									color: "#6c6c6c",
									"&:hover": {
										color: "#000000",
									},
								}}
								onClick={() => handleEditClick("crossPatternMovement")}
							/>
						</Tooltip>
					</Box>
				</Box>
			</Box>

			{/* Edit Dialog */}
			{editingScope && (
				<WorkerMovementEditDialog
					open={true}
					onClose={handleDialogClose}
					scope={editingScope}
					initialValues={workerMovementEligibility}
					workerTypes={workerTypes.length > 0 ? workerTypes?.filter((wt:any) => wt.id === 1) : []}
					workerSubTypes={workerSubTypes.length > 0 ? workerSubTypes?.filter((st:any) => st.workerType.id === 1) : []}
					onSave={handleDialogSave}
					isSaving={updateScheduleConfigurationStatus === "pending"}
				/>
			)}

			{/* Worker Movement Update Success */}
			<Snackbar
				open={updateScheduleConfigurationStatus === "fulfilled"}
				autoHideDuration={1000}
				onClose={() => {
					dispatch(resetUpdateScheduleConfiguration());
					setEditingScope(null);
				}}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant="filled" severity="success">
					Worker movement settings updated successfully
				</MuiAlert>
			</Snackbar>

			{/* Worker Movement Update Error */}
			<Snackbar
				open={updateScheduleConfigurationStatus === "error"}
				autoHideDuration={2000}
				onClose={() => {
					dispatch(resetUpdateScheduleConfiguration());
				}}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<MuiAlert elevation={6} variant="filled" severity="error">
					{updateScheduleConfigurationError || "Failed to update worker movement settings"}
				</MuiAlert>
			</Snackbar>
		</Box>
	);
};

export default WorkerMovementSettings;
